/**
 * Calculs en attente de machine — administrateurs de la plateforme.
 *
 * AWS Batch ne fait pas passer un calcul du parc Spot au parc à la demande
 * quand le Spot n'a aucune machine : le calcul attend indéfiniment. Cette page
 * dit lesquels attendent, pourquoi, et ce que coûterait de les relancer à la
 * demande. La décision reste à l'admin (choix du fondateur, 5 novembre 2026).
 *
 * Le mail de veille envoyé aux superadmins pointe ici (/superadmin/en-attente).
 *
 * Le privilège est appliqué côté serveur (403) et par SuperadminRoute. Ce
 * fichier ne garde rien : il affiche ce qu'on lui a donné le droit de lire.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import simulationService from '../../services/simulationService';

// Assez court pour voir un calcul démarrer sans recharger, assez long pour que
// chaque lecture (plusieurs appels AWS côté serveur) reste négligeable.
const PERIODE_MS = 45000;

const PendingSimulationsAdminPage = () => {
  const { t, i18n } = useTranslation();
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [actualiseLe, setActualiseLe] = useState(null);
  const [aConfirmer, setAConfirmer] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [retour, setRetour] = useState(null);
  const enCours = useRef(false);

  const charger = useCallback(async () => {
    // Une lecture à la fois : un serveur lent ne doit pas empiler les appels.
    if (enCours.current) return;
    enCours.current = true;
    try {
      setDonnees(await simulationService.getPendingSimulations());
      setErreur(null);
      setActualiseLe(new Date());
    } catch (err) {
      setErreur(err.response?.status === 403 ? 'refuse' : 'echec');
    } finally {
      enCours.current = false;
      setChargement(false);
    }
  }, []);

  // Actualisation périodique, suspendue tant que l'onglet n'est pas visible.
  useEffect(() => {
    let minuterie = null;
    const demarrer = () => {
      if (minuterie === null) minuterie = setInterval(charger, PERIODE_MS);
    };
    const arreter = () => {
      if (minuterie !== null) {
        clearInterval(minuterie);
        minuterie = null;
      }
    };
    const surVisibilite = () => {
      if (document.hidden) {
        arreter();
      } else {
        charger();
        demarrer();
      }
    };

    charger();
    if (!document.hidden) demarrer();
    document.addEventListener('visibilitychange', surVisibilite);
    return () => {
      arreter();
      document.removeEventListener('visibilitychange', surVisibilite);
    };
  }, [charger]);

  const nombre = (valeur, decimales = 2) =>
    new Intl.NumberFormat(i18n.language, {
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales,
    }).format(valeur);

  const usd = (valeur) =>
    valeur === null || valeur === undefined ? '—' : t('enAttente.usd', { montant: nombre(valeur) });

  const jour = (iso) =>
    new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'numeric' });

  const texteRaison = (raison) => {
    if (!raison) return '';
    const p = raison.parametres || {};
    const cle =
      raison.code === 'spot_indisponible' && p.taille === 'xlarge'
        ? 'spot_indisponible_xlarge'
        : raison.code;
    return t(`enAttente.raisons.${cle}`, {
      defaultValue: raison.texte,
      vcpu: p.vcpu,
      gib: p.memoire_gib,
    });
  };

  const texteEstimation = (e) => {
    if (!e || !e.machine) return t('enAttente.aucuneMachine');
    const machine = t('enAttente.machine', {
      type: e.machine.type,
      prix: usd(e.machine.prix_horaire_usd),
    });
    const morceaux = [];
    if (e.duree?.source === 'mesure') {
      morceaux.push(
        t('enAttente.estimationMesuree', {
          minutes: nombre(e.duree.minutes, 0),
          machine,
          cout: usd(e.cout_estime_usd),
          date: e.duree.mesure_le ? jour(e.duree.mesure_le) : '?',
        })
      );
    } else {
      morceaux.push(t('enAttente.estimationSansMesure', { machine }));
    }
    if (e.plafond_usd !== null && e.plafond_usd !== undefined) {
      morceaux.push(
        t('enAttente.plafond', {
          montant: usd(e.plafond_usd),
          heures: nombre(e.timeout_h, 0),
        })
      );
    }
    if (e.cout_spot_equivalent_usd !== null && e.cout_spot_equivalent_usd !== undefined) {
      morceaux.push(
        e.base_spot === 'plafond'
          ? t('enAttente.spotSurPlafond', { montant: usd(e.cout_spot_equivalent_usd) })
          : t('enAttente.spot', { montant: usd(e.cout_spot_equivalent_usd) })
      );
    }
    return morceaux.join(' · ');
  };

  const relancer = async () => {
    if (!aConfirmer) return;
    const cible = aConfirmer;
    setEnvoi(true);
    setRetour(null);
    try {
      await simulationService.relaunchOnDemand(
        cible.simulation_id,
        cible.estimation.montant_a_confirmer_usd
      );
      setRetour({ ok: true, texte: t('enAttente.relanceOk', { id: cible.simulation_id }) });
    } catch (err) {
      const d = err.response?.data || {};
      setRetour({
        ok: false,
        texte: t(`enAttente.refus.${d.code || 'inconnu'}`, {
          defaultValue: d.message || t('enAttente.refus.inconnu'),
          montant: usd(d.montant_actuel_usd),
        }),
      });
    } finally {
      setEnvoi(false);
      setAConfirmer(null);
      charger();
    }
  };

  const simulations = donnees?.simulations || [];
  const horsPlateforme = donnees?.hors_plateforme || [];
  const erreursFiles = donnees?.erreurs || [];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('enAttente.titre')}</h1>
          <p className="text-dark-400 text-sm mt-1">{t('enAttente.intro')}</p>
          {donnees?.seuils_min && (
            <p className="text-dark-500 text-xs mt-1">
              {t('enAttente.seuils', {
                xlarge: donnees.seuils_min.xlarge,
                large: donnees.seuils_min.large,
              })}
            </p>
          )}
        </div>
        <div className="shrink-0 text-right">
          <button
            onClick={charger}
            disabled={chargement}
            className="px-4 py-2 text-sm rounded-lg bg-dark-800 border border-dark-700 text-dark-200 hover:bg-dark-700 disabled:opacity-50"
          >
            {chargement ? t('enAttente.chargement') : t('enAttente.rafraichir')}
          </button>
          {actualiseLe && (
            <p className="text-dark-600 text-xs mt-1">
              {t('enAttente.actualiseLe', { heure: actualiseLe.toLocaleTimeString(i18n.language) })}
            </p>
          )}
        </div>
      </div>

      {retour && (
        <div
          className={`rounded-lg p-4 border ${
            retour.ok
              ? 'bg-success-500/10 border-success-500/30 text-success-400'
              : 'bg-error-500/10 border-error-500/30 text-error-400'
          }`}
        >
          <p className="text-sm">{retour.texte}</p>
        </div>
      )}

      {erreur && (
        <div className="bg-error-500/10 border border-error-500/30 rounded-lg p-4">
          <p className="text-error-400 text-sm">{t(`enAttente.${erreur}`)}</p>
        </div>
      )}

      {erreursFiles.map((e) => (
        <div key={e.file} className="bg-warning-500/10 border border-warning-500/30 rounded-lg p-3">
          <p className="text-warning-400 text-sm">{t('enAttente.fileIllisible', { file: e.file })}</p>
        </div>
      ))}

      {!erreur && !chargement && simulations.length === 0 && (
        <div className="bg-dark-800 border border-dark-700 rounded-lg p-6">
          <p className="text-dark-300 text-sm">{t('enAttente.aucun')}</p>
        </div>
      )}

      {simulations.length > 0 && (
        <section className="space-y-3">
          {simulations.map((s) => (
            <div
              key={s.job?.id || s.simulation_id}
              className={`border rounded-lg p-4 space-y-3 ${
                s.depasse_seuil
                  ? 'bg-error-500/5 border-error-500/30'
                  : 'bg-dark-800 border-dark-700'
              }`}
            >
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="text-dark-100 font-medium">{s.case?.label || s.case?.id}</span>
                {s.projet && (
                  <span className="text-dark-300 text-sm">
                    {s.projet.nom || s.projet.id}
                  </span>
                )}
                {s.tache && (
                  <span className="text-xs px-2 py-0.5 rounded bg-dark-900 text-dark-300 font-mono">
                    {s.tache}
                    {s.budget ? ` · ${s.budget}` : ''}
                  </span>
                )}
                <span className="text-dark-500 text-xs font-mono">{s.simulation_id}</span>
                {s.attente_min !== null && s.attente_min !== undefined && (
                  <span
                    className={`text-sm ml-auto ${
                      s.depasse_seuil ? 'text-error-400 font-semibold' : 'text-dark-400'
                    }`}
                  >
                    {t('enAttente.attente', {
                      minutes: nombre(s.attente_min, 0),
                      seuil: s.seuil_min,
                      file: s.file?.taille,
                    })}
                  </span>
                )}
              </div>

              {s.erreur ? (
                <p className="text-warning-400 text-sm">{t('enAttente.analyseIncomplete')}</p>
              ) : (
                <>
                  <div>
                    <p className="text-dark-100 text-sm">{texteRaison(s.raison)}</p>
                    {s.raison?.tentatives_echouees > 0 && (
                      <p className="text-dark-500 text-xs mt-1">
                        {t('enAttente.tentatives', { count: s.raison.tentatives_echouees })}
                      </p>
                    )}
                  </div>

                  <p className="text-dark-200 text-sm font-mono">{texteEstimation(s.estimation)}</p>

                  <details className="text-xs">
                    <summary className="text-dark-500 cursor-pointer hover:text-dark-300">
                      {t('enAttente.detailAws')}
                    </summary>
                    <dl className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 font-mono text-dark-400">
                      <dt className="text-dark-500">{t('enAttente.champs.job')}</dt>
                      <dd className="break-all">
                        {s.job?.id} ({s.job?.statut})
                      </dd>
                      <dt className="text-dark-500">{t('enAttente.champs.file')}</dt>
                      <dd className="break-all">
                        {s.file?.nom} → {s.file?.a_la_demande}
                      </dd>
                      <dt className="text-dark-500">{t('enAttente.champs.reservation')}</dt>
                      <dd>
                        {t('enAttente.champs.reservationValeur', {
                          vcpu: s.reservation?.vcpu ?? '—',
                          mio: s.reservation?.memoire_mib ?? '—',
                          heures: s.reservation?.timeout_h ?? '—',
                        })}
                      </dd>
                      <dt className="text-dark-500">{t('enAttente.champs.raisonJob')}</dt>
                      <dd className="break-all">{s.raison?.brute?.job || '—'}</dd>
                      <dt className="text-dark-500">{t('enAttente.champs.raisonParc')}</dt>
                      <dd className="break-all">{s.raison?.brute?.parc_spot || '—'}</dd>
                      <dt className="text-dark-500">{t('enAttente.champs.derniereTentative')}</dt>
                      <dd>
                        {s.raison?.brute?.derniere_tentative
                          ? new Date(s.raison.brute.derniere_tentative).toLocaleString(i18n.language)
                          : '—'}
                      </dd>
                      <dt className="text-dark-500">{t('enAttente.champs.environnement')}</dt>
                      <dd className="break-all">
                        {s.raison?.brute?.environnement || '—'}
                        {s.raison?.brute?.parc_lisible === false
                          ? ` (${t('enAttente.parcIllisible')})`
                          : ''}
                      </dd>
                      {s.estimation?.duree?.simulation_id && (
                        <>
                          <dt className="text-dark-500">{t('enAttente.champs.mesure')}</dt>
                          <dd className="break-all">{s.estimation.duree.simulation_id}</dd>
                        </>
                      )}
                      {(s.estimation?.machines_sans_prix || []).length > 0 && (
                        <>
                          <dt className="text-dark-500">{t('enAttente.champs.sansPrix')}</dt>
                          <dd>{s.estimation.machines_sans_prix.join(', ')}</dd>
                        </>
                      )}
                    </dl>
                  </details>
                </>
              )}

              <div className="flex justify-end">
                {s.relance_possible ? (
                  <button
                    onClick={() => {
                      setRetour(null);
                      setAConfirmer(s);
                    }}
                    disabled={envoi}
                    className="px-4 py-2 text-sm rounded-lg bg-primary-500/20 text-primary-400 hover:bg-primary-500/30 disabled:opacity-50"
                  >
                    {t('enAttente.relancer')}
                  </button>
                ) : (
                  <p className="text-dark-500 text-xs">
                    {t(`enAttente.impossible.${s.relance_impossible || 'inconnu'}`, {
                      defaultValue: t('enAttente.impossible.inconnu'),
                    })}
                  </p>
                )}
              </div>
            </div>
          ))}
        </section>
      )}

      {horsPlateforme.length > 0 && (
        <section>
          <h2 className="text-dark-100 font-semibold mb-1">
            {t('enAttente.horsPlateforme')} ({horsPlateforme.length})
          </h2>
          <p className="text-dark-500 text-xs mb-3">{t('enAttente.horsPlateformeAide')}</p>
          <div className="space-y-2">
            {horsPlateforme.map((h) => (
              <div
                key={h.job?.id}
                className="bg-dark-800 border border-dark-700 rounded-lg px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1"
              >
                <span className="text-dark-300 text-sm font-mono break-all">{h.job?.nom}</span>
                <span className="text-dark-500 text-xs">{h.file?.taille}</span>
                {h.raison && <span className="text-dark-400 text-sm">{texteRaison(h.raison)}</span>}
                {h.attente_min !== null && h.attente_min !== undefined && (
                  <span
                    className={`text-sm ml-auto ${
                      h.depasse_seuil ? 'text-error-400 font-semibold' : 'text-dark-400'
                    }`}
                  >
                    {t('enAttente.attente', {
                      minutes: nombre(h.attente_min, 0),
                      seuil: h.seuil_min,
                      file: h.file?.taille,
                    })}
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {aConfirmer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 border border-dark-700 rounded-xl p-6 max-w-md w-full space-y-4">
            <h2 className="text-xl font-semibold text-dark-100">{t('enAttente.confirmerTitre')}</h2>
            <p className="text-dark-300 text-sm">
              {t('enAttente.confirmerTexte', {
                case: aConfirmer.case?.label || aConfirmer.case?.id,
                projet: aConfirmer.projet?.nom || aConfirmer.projet?.id || '—',
                type: aConfirmer.estimation?.machine?.type,
              })}
            </p>
            <div className="bg-dark-900 rounded-lg p-3">
              <p className="text-dark-500 text-xs uppercase tracking-wider mb-1">
                {aConfirmer.estimation?.duree?.source === 'mesure'
                  ? t('enAttente.montantEstime')
                  : t('enAttente.montantPlafond')}
              </p>
              <p className="text-dark-100 text-2xl font-mono">
                {usd(aConfirmer.estimation?.montant_a_confirmer_usd)}
              </p>
              {aConfirmer.estimation?.duree?.source === 'mesure' && (
                <p className="text-dark-500 text-xs mt-1">
                  {t('enAttente.plafond', {
                    montant: usd(aConfirmer.estimation.plafond_usd),
                    heures: nombre(aConfirmer.estimation.timeout_h, 0),
                  })}
                </p>
              )}
            </div>
            <p className="text-dark-500 text-xs">{t('enAttente.confirmerAvertissement')}</p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setAConfirmer(null)}
                disabled={envoi}
                className="px-4 py-2 bg-dark-700 hover:bg-dark-600 text-dark-200 rounded-lg transition-colors disabled:opacity-50"
              >
                {t('enAttente.annuler')}
              </button>
              <button
                type="button"
                onClick={relancer}
                disabled={envoi}
                className="px-4 py-2 bg-primary-500/20 text-primary-400 hover:bg-primary-500/30 rounded-lg transition-colors disabled:opacity-50"
              >
                {envoi ? t('enAttente.envoi') : t('enAttente.confirmer')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingSimulationsAdminPage;
